// hooks/useAddresses.js
// Addresses Hook - Synchronized with Mobile App
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";

export const useAddresses = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch addresses - only when authenticated
  const {
    data: addresses = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["addresses"],
    queryFn: async () => {
      const response = await apiService.getAddresses();
      if (response.status === "success" && response.data) {
        return response.data;
      }
      return [];
    },
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes - addresses don't change often
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });

  // Add address mutation
  const addAddressMutation = useMutation({
    mutationFn: (addressData) => apiService.addAddress(addressData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
    },
  });

  // Update address mutation
  const updateAddressMutation = useMutation({
    mutationFn: ({ id, addressData }) =>
      apiService.updateAddress(id, addressData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
    },
  });

  // Delete address mutation
  const deleteAddressMutation = useMutation({
    mutationFn: (addressId) => apiService.deleteAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
    },
  });

  // Set default address mutation
  const setDefaultMutation = useMutation({
    mutationFn: (addressId) => apiService.setDefaultAddress(addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addresses"] });
    },
  });

  // Get default address
  const defaultAddress = addresses.find((addr) => addr.isDefault) || addresses[0];

  return {
    // Data
    addresses,
    defaultAddress,
    addressCount: addresses.length,

    // States
    isLoading,
    isError,
    error,

    // Actions
    refetch,
    addAddress: addAddressMutation.mutateAsync,
    updateAddress: updateAddressMutation.mutateAsync,
    deleteAddress: deleteAddressMutation.mutate,
    setDefaultAddress: setDefaultMutation.mutate,

    // Mutation states
    isAddingAddress: addAddressMutation.isPending,
    isUpdatingAddress: updateAddressMutation.isPending,
    isDeletingAddress: deleteAddressMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending,
  };
};

export default useAddresses;

